<template>
  <div class="page">
    <header class="head">
      <div class="title-wrap">
        <h2 class="h2">담당자 관리</h2>
        <p class="sub">테크센터 관리자 계정과 접근 권한을 관리합니다.</p>
      </div>

      <div class="head-actions">
        <div class="member-summary" aria-label="조회된 담당자 수">
          <span>전체 담당자</span>
          <strong>{{ filteredMembers.length }}</strong>
          <em>명</em>
        </div>
        <button
          v-if="u_id === 'administrator@dincon.co.kr'"
          class="btn btn-primary"
          type="button"
          @click="openCreate"
        >
          <span aria-hidden="true">＋</span> 담당자 등록
        </button>
      </div>
    </header>

    <section class="panel">
      <div class="toolbar">
        <div class="tool-left">
          <input
            v-model.trim="q"
            class="input"
            placeholder="이름 또는 이메일 검색"
            aria-label="담당자 검색"
          />
        </div>

        <div class="tool-right">
          <button class="btn btn-ghost" type="button" @click="resetFilters">
            필터 초기화
          </button>
        </div>
      </div>

      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th class="col-name">이름</th>
              <th class="col-id">아이디(이메일)</th>
              <th
                class="col-actions right"
                v-if="u_id === 'administrator@dincon.co.kr'"
              >
                작업
              </th>
            </tr>
          </thead>

          <tbody v-if="filteredMembers.length > 0">
            <tr v-for="m in filteredMembers" :key="m.u_id">
              <td>
                <div class="member-profile">
                  <span class="member-avatar">{{
                    memberInitial(m.u_name)
                  }}</span>
                  <div>
                    <div class="strong">{{ m.u_name }}</div>
                    <span class="profile-label">담당자</span>
                  </div>
                </div>
              </td>

              <td>
                <span class="email-value">{{ m.u_id }}</span>
              </td>

              <td v-if="u_id === 'administrator@dincon.co.kr'" class="right">
                <div class="actions">
                  <button class="link" type="button" @click="openReset(m)">
                    비밀번호 초기화
                  </button>
                  <button
                    class="link danger"
                    type="button"
                    @click="withdraw(m)"
                  >
                    탈퇴
                  </button>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td
                class="empty"
                :colspan="u_id === 'administrator@dincon.co.kr' ? 3 : 2"
              >
                결과가 없습니다. 검색어/필터를 확인하거나 사용자를 등록하세요.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Create Modal -->
    <div v-if="showCreate" class="modal-overlay" @click.self="closeCreate">
      <div class="modal">
        <header class="modal-header">
          <div class="modal-heading">
            <div class="modal-icon" aria-hidden="true">＋</div>
            <div>
              <div class="modal-title">담당자 등록</div>
              <p class="modal-subtitle">새로운 관리자 계정을 생성합니다.</p>
            </div>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="모달 닫기"
            @click="closeCreate"
          >
            ×
          </button>
        </header>

        <div class="modal-body">
          <label class="field">
            <div class="label">아이디(이메일)</div>
            <input
              v-model.trim="createForm.email"
              class="input mono"
              placeholder="예) user@dincon.co.kr"
            />
            <small v-if="createErrors.email" class="error">{{
              createErrors.email
            }}</small>
          </label>

          <label class="field">
            <div class="label">비밀번호</div>
            <input
              type="password"
              v-model.trim="createForm.password"
              class="input mono"
            />
            <small v-if="createErrors.password" class="error">{{
              createErrors.password
            }}</small>
          </label>

          <label class="field">
            <div class="label">비밀번호 확인</div>
            <input
              type="password"
              v-model.trim="createForm.passwordChk"
              class="input mono"
            />
            <small v-if="createErrors.passwordChk" class="error">{{
              createErrors.passwordChk
            }}</small>
          </label>

          <label class="field">
            <div class="label">이름</div>
            <input
              v-model.trim="createForm.name"
              class="input"
              placeholder="예) 홍길동"
            />
            <small v-if="createErrors.name" class="error">{{
              createErrors.name
            }}</small>
          </label>
        </div>

        <footer class="modal-actions">
          <button class="btn btn-ghost" type="button" @click="closeCreate">
            취소
          </button>
          <button class="btn btn-primary" type="button" @click="createMember">
            등록
          </button>
        </footer>
      </div>
    </div>

    <!-- Reset Password Modal -->
    <div v-if="showReset" class="modal-overlay" @click.self="closeReset">
      <div class="modal">
        <header class="modal-header">
          <div class="modal-heading">
            <div class="modal-icon reset" aria-hidden="true">↻</div>
            <div>
              <div class="modal-title">비밀번호 초기화</div>
              <p class="modal-subtitle">
                담당자의 로그인 비밀번호를 재설정합니다.
              </p>
            </div>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="모달 닫기"
            @click="closeReset"
          >
            ×
          </button>
        </header>

        <div class="modal-body">
          <div class="target-card">
            <span class="target-avatar">{{
              memberInitial(resetTarget?.u_name)
            }}</span>
            <div class="target-info">
              <span>초기화 대상</span>
              <strong>{{ resetTarget?.u_name }}</strong>
              <small>{{ resetTarget?.u_id }}</small>
            </div>
          </div>

          <div class="notice-card">
            <span class="notice-icon" aria-hidden="true">i</span>
            <div>
              <strong>초기화 후 안내</strong>
              <p>임시 비밀번호가 담당자의 이메일로 발급됩니다.</p>
            </div>
          </div>
        </div>

        <footer class="modal-actions">
          <button class="btn btn-ghost" type="button" @click="closeReset">
            취소
          </button>
          <button class="btn btn-primary" type="button" @click="resetPassword">
            비밀번호 초기화
          </button>
        </footer>
      </div>
    </div>

    <!-- Withdraw Modal -->
    <div v-if="showWithdraw" class="modal-overlay" @click.self="closeWithdraw">
      <div class="modal danger-modal">
        <header class="modal-header danger-header">
          <div class="modal-heading">
            <div class="modal-icon danger" aria-hidden="true">!</div>
            <div>
              <div class="modal-title">담당자 탈퇴</div>
              <p class="modal-subtitle">
                계정을 삭제하기 전 대상을 확인해 주세요.
              </p>
            </div>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="모달 닫기"
            @click="closeWithdraw"
          >
            ×
          </button>
        </header>

        <div class="modal-body">
          <div class="target-card danger">
            <span class="target-avatar danger">{{
              memberInitial(withdrawTarget?.u_name)
            }}</span>
            <div class="target-info">
              <span>탈퇴 대상</span>
              <strong>{{ withdrawTarget?.u_name }}</strong>
              <small>{{ withdrawTarget?.u_id }}</small>
            </div>
          </div>

          <div class="notice-card danger">
            <span class="notice-icon danger" aria-hidden="true">!</span>
            <div>
              <strong>이 작업은 되돌리기 어렵습니다.</strong>
              <p>탈퇴 처리 후 해당 계정으로 로그인할 수 없습니다.</p>
            </div>
          </div>
        </div>

        <footer class="modal-actions">
          <button class="btn btn-ghost" type="button" @click="closeWithdraw">
            취소
          </button>
          <button class="btn btn-danger" type="button" @click="doWithdraw">
            담당자 탈퇴
          </button>
        </footer>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="toast.show" class="toast">{{ toast.message }}</div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, onMounted } from "vue";
import Cookies from "js-cookie";

onMounted(() => {
  getMember();
});

const u_id = Cookies.get("u_id");

/**
 * 사용자 조회
 */
const members = ref([]);
async function getMember() {
  const result = await memberApi.getMember();
  members.value = result;
}

/**
 * ✅ 필터
 */
const q = ref("");
const statusFilter = ref("all");
const roleFilter = ref("all");

const filteredMembers = computed(() => {
  let list = members.value;

  const keyword = q.value.trim().toLowerCase();
  if (keyword) {
    list = list.filter((m) => {
      return (
        m.u_name.toLowerCase().includes(keyword) ||
        m.u_id.toLowerCase().includes(keyword) ||
        String(m.department || "")
          .toLowerCase()
          .includes(keyword) ||
        roleLabel(m.role).toLowerCase().includes(keyword)
      );
    });
  }

  if (statusFilter.value !== "all") {
    const target = statusFilter.value === "active";
    list = list.filter((m) => m.active === target);
  }

  if (roleFilter.value !== "all") {
    list = list.filter((m) => m.role === roleFilter.value);
  }

  return [...list].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
});

function resetFilters() {
  q.value = "";
  statusFilter.value = "all";
  roleFilter.value = "all";
}

function memberInitial(name) {
  return String(name || "?")
    .trim()
    .charAt(0)
    .toUpperCase();
}

/**
 * ✅ 라벨
 */
function roleLabel(r) {
  return (
    {
      ADMIN: "관리자",
      MANAGER: "매니저",
      VIEWER: "조회"
    }[r] || "-"
  );
}

/**
 * ✅ 사용자 등록
 */
const showCreate = ref(false);
const createForm = reactive({
  name: "",
  email: "",
  password: "",
  passwordChk: ""
});
const createErrors = reactive({
  name: "",
  email: "",
  password: "",
  passwordChk: ""
});

function openCreate() {
  showCreate.value = true;
  createForm.name = "";
  createForm.email = "";
  createForm.password = "";
  createForm.passwordChk = "";
  createErrors.name = "";
  createErrors.email = "";
  createErrors.password = "";
  createErrors.passwordChk = "";
}

function closeCreate() {
  showCreate.value = false;
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

async function createMember() {
  createErrors.email = "";
  createErrors.password = "";
  createErrors.passwordChk = "";
  createErrors.name = "";

  if (!createForm.email || !isValidEmail(createForm.email)) {
    createErrors.email = "올바른 이메일을 입력해주세요.";
  } else {
    const flag = await checkEmail(createForm.email);
    if (!flag) {
      createErrors.email = "이미 존재하는 이메일입니다.";
    }
  }

  if (!createForm.password || createForm.password.length < 4) {
    createErrors.password = "비밀번호는 4자 이상 입력해주세요.";
  }

  if (createForm.password !== createForm.passwordChk) {
    createErrors.passwordChk = "비밀번호와 비밀번호 확인이 서로 맞지 않습니다.";
  }

  if (!createForm.name || createForm.name.length < 2) {
    createErrors.name = "이름은 2자 이상 입력해주세요.";
  }

  if (
    createErrors.name ||
    createErrors.email ||
    createErrors.password ||
    createErrors.passwordChk
  )
    return;

  const newMember = {
    id: createForm.email,
    pw: createForm.password,
    name: createForm.name
  };
  showCreate.value = false;

  const result = await memberApi.createMember(newMember);
  if (result) {
    showToast("사용자 등록 완료");
  }
}

/**
 * 아이디 중복 체크
 */
import { memberApi } from "@/api";
async function checkEmail(email) {
  const isEnabled = await memberApi.checkUserIdEnabled(email);
  return isEnabled;
}

/**
 * ✅ 비밀번호 초기화
 */
const showReset = ref(false);
const resetTarget = ref(null);

function openReset(member) {
  resetTarget.value = member;
  showReset.value = true;
}

function closeReset() {
  showReset.value = false;
  resetTarget.value = null;
}

async function resetPassword() {
  if (!resetTarget.value) return;

  // 실제 구현: API 호출 + 이메일 발송/초기화 링크
  const result = await memberApi.initPassword(resetTarget.value.u_id);
  if (result) {
    showReset.value = false;
    resetTarget.value = null;
    showToast(`비밀번호가 초기화 되었습니다.`);
  }
}

/**
 * ✅ 탈퇴
 */
const showWithdraw = ref(false);
const withdrawTarget = ref(null);

function withdraw(member) {
  withdrawTarget.value = member;
  showWithdraw.value = true;
}

function closeWithdraw() {
  showWithdraw.value = false;
  withdrawTarget.value = null;
}

async function doWithdraw() {
  if (!withdrawTarget.value) return;

  // const idx = members.value.findIndex((m) => m.u_id === withdrawTarget.value.u_id);
  // if (idx >= 0) members.value.splice(idx, 1);

  const result = await memberApi.deleteMember(withdrawTarget.value.u_id);
  if (result) {
    showWithdraw.value = false;
    withdrawTarget.value = null;
    showToast("탈퇴 처리됨");
  }
}

/**
 * ✅ Toast
 */
const toast = reactive({
  show: false,
  message: ""
});
let toastTimer = null;

function showToast(msg) {
  toast.message = msg;
  toast.show = true;

  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast.show = false;
    toast.message = "";
  }, 2200);
}
</script>

<style scoped>
.page {
  background: #f4f6f8;
  display: grid;
  gap: 14px;
}

.head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.h2 {
  margin: 0;
  font-size: 18px;
}

.sub {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
}

.sep {
  margin: 0 8px;
  color: #bbb;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
}

.strong {
  font-weight: 900;
}

.muted {
  color: #777;
  font-size: 12px;
  margin-top: 2px;
}

.head-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.panel {
  background: #fff;
  border: 1px solid #e6e8eb;
  border-radius: 16px;
  padding: 14px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef0f2;
}

.tool-left {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.tool-right {
  display: flex;
  gap: 10px;
  align-items: center;
}

.input {
  border: 1px solid #e6e8eb;
  border-radius: 10px;
  padding: 10px 10px;
  font-size: 13px;
  background: #fff;
  min-width: 240px;
}

.select {
  min-width: 160px;
}

.table-wrap {
  margin-top: 12px;
  border: 1px solid #eef0f2;
  border-radius: 12px;
  overflow: hidden;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.table thead th {
  background: #fafbfc;
  text-align: left;
  padding: 10px 10px;
  border-bottom: 1px solid #eef0f2;
  color: #444;
  font-weight: 800;
}

.table tbody td {
  padding: 10px 10px;
  border-bottom: 1px solid #f0f2f4;
  vertical-align: top;
}

.table tbody tr:hover {
  background: #fafbfc;
}

.right {
  text-align: right;
}

.col-actions {
  width: 290px;
}

.actions {
  display: inline-flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
  align-items: center;
}

.link {
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: #1f2a6d;
  font-weight: 900;
  font-size: 12px;
}

.link:hover {
  text-decoration: underline;
}

.link.danger {
  color: #b42318;
}

.badge {
  display: inline-flex;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid transparent;
  white-space: nowrap;
  font-weight: 900;
}

.b-on {
  background: #e6f7ee;
  color: #137333;
  border-color: #bbf7d0;
}

.b-off {
  background: #f3f4f6;
  color: #444;
  border-color: #e6e8eb;
}

.chip {
  display: inline-flex;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid #e6e8eb;
  white-space: nowrap;
}

.c-admin {
  background: #111;
  color: #fff;
  border-color: #111;
}

.c-manager {
  background: #eef2ff;
  color: #1f2a6d;
  border-color: #dbe3ff;
}

.c-viewer {
  background: #f3f4f6;
  color: #444;
  border-color: #e6e8eb;
}

.empty {
  text-align: center;
  color: #666;
  padding: 18px 10px;
}

.foot-note {
  margin-top: 10px;
  font-size: 12px;
  color: #666;
}

/* buttons */
.btn {
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
}

.btn-primary {
  background: #111;
  color: #fff;
}

.btn-outline {
  background: #fff;
  border-color: #e6e8eb;
  color: #111;
}

.btn-outline:hover {
  background: #f3f4f6;
}

.btn-outline.danger {
  border-color: #f1c0c0;
  color: #b42318;
}

.btn-ghost {
  background: transparent;
  border-color: transparent;
  color: #555;
}

.btn-ghost:hover {
  background: #f3f4f6;
}

/* modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 50;
}

.modal {
  width: 460px;
  max-width: 100%;
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #e6e8eb;
}

.modal.wide {
  width: 860px;
}

.modal-title {
  font-size: 16px;
  font-weight: 900;
  margin-bottom: 12px;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.field {
  display: grid;
  gap: 6px;
  margin-top: 10px;
}

.label {
  font-size: 13px;
  color: #555;
}

.error {
  color: #d93025;
  font-size: 12px;
}

.hint {
  margin-top: 10px;
  font-size: 12px;
  color: #666;
}

.modal-actions {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.reset-summary {
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #eef0f2;
  border-radius: 12px;
  background: #fafbfc;
  margin-bottom: 10px;
}

.reset-summary .k {
  font-size: 12px;
  color: #666;
}

.warning {
  border: 1px solid #eef0f2;
  background: #fafbfc;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 13px;
  color: #333;
}

.warning.danger {
  border-color: #f1c0c0;
  background: #fff5f5;
  color: #b42318;
}

/* toast */
.toast {
  position: fixed;
  right: 18px;
  bottom: 18px;
  background: rgba(17, 17, 17, 0.92);
  color: #fff;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 13px;
  z-index: 60;
}

@media (max-width: 980px) {
  .modal.wide {
    width: 96vw;
  }
  .grid {
    grid-template-columns: 1fr;
  }
  .col-dept,
  .col-date {
    display: none;
  }
  .input {
    min-width: 180px;
  }
}

/* refreshed member management */
.page {
  min-height: 100%;
  box-sizing: border-box;
  padding: 24px 20px 40px;
  background: #f4f7fb;
  color: #1f2937;
  align-content: start;
  gap: 20px;
}

.head {
  align-items: flex-end;
  gap: 24px;
}

.eyebrow {
  margin-bottom: 6px;
  color: #2563eb;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.h2 {
  color: #111827;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.sub {
  margin: 7px 0 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
}

.head-actions {
  align-items: center;
  gap: 16px;
  flex-wrap: nowrap;
}

.head-actions .btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 48px;
  padding: 0 17px;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.2);
}

.member-summary {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-height: 42px;
  box-sizing: border-box;
  padding: 0;
  color: #737373;
  font-size: 12px;
  white-space: nowrap;
  line-height: 42px;
}

.member-summary strong {
  margin-left: 6px;
  color: #171717;
  font-size: 22px;
  line-height: 1;
}

.member-summary em {
  color: #555;
  font-style: normal;
}

.panel {
  padding: 18px;
  border-color: #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
}

.toolbar {
  padding-bottom: 16px;
  border-bottom-color: #edf1f6;
}

.tool-left {
  flex: 1;
}

.input {
  height: 42px;
  box-sizing: border-box;
  padding: 0 13px;
  border-color: #dbe2ea;
  border-radius: 11px;
  outline: none;
  background: #f8fafc;
  color: #1f2937;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background 0.18s ease;
}

.tool-left .input {
  width: min(100%, 420px);
}

.input:focus {
  border-color: #3b82f6;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.input::placeholder {
  color: #94a3b8;
}

.table-wrap {
  margin-top: 16px;
  overflow-x: auto;
  border-color: #e5eaf0;
  border-radius: 14px;
}

.table {
  min-width: 700px;
}

.table thead th {
  padding: 13px 16px;
  border-bottom-color: #e5eaf0;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.table tbody td {
  padding: 15px 16px;
  border-bottom-color: #edf1f5;
  vertical-align: middle;
}

.table tbody tr:last-child td {
  border-bottom: 0;
}

.table tbody tr:hover {
  background: #f8fbff;
}

.col-name {
  width: 34%;
}

.col-id {
  width: 38%;
}

.col-actions {
  width: 280px;
}

.member-profile {
  display: flex;
  align-items: center;
  gap: 11px;
}

.member-avatar,
.target-avatar {
  display: grid;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  place-items: center;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  background: linear-gradient(145deg, #dbeafe, #eff6ff);
  color: #1d4ed8;
  font-size: 14px;
  font-weight: 800;
}

.strong {
  color: #111827;
  font-weight: 700;
}

.profile-label {
  display: block;
  margin-top: 2px;
  color: #94a3b8;
  font-size: 11px;
}

.email-value {
  color: #475569;
  font-size: 13px;
}

.actions {
  gap: 8px;
}

.link {
  min-height: 32px;
  padding: 7px 10px;
  border: 1px solid #dbe4f0;
  border-radius: 9px;
  background: #fff;
  color: #334155;
  font-weight: 700;
  transition: border-color 0.18s ease, color 0.18s ease, background 0.18s ease;
}

.link:hover {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
  text-decoration: none;
}

.link.danger {
  border-color: #fee2e2;
  color: #b91c1c;
}

.link.danger:hover {
  border-color: #fecaca;
  background: #fff1f2;
  color: #991b1b;
}

.empty {
  padding: 54px 16px !important;
  color: #64748b;
}

.btn {
  min-height: 40px;
  padding: 9px 13px;
  font-weight: 700;
}

.btn-primary {
  background: #1d4ed8;
}

.btn-ghost {
  border-color: #dbe2ea;
  background: #fff;
  color: #475569;
}

.btn-ghost:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.btn-danger {
  border-color: #dc2626;
  background: #dc2626;
  color: #fff;
  box-shadow: 0 5px 12px rgba(220, 38, 38, 0.18);
}

.btn-danger:hover {
  border-color: #b91c1c;
  background: #b91c1c;
}

.modal-overlay {
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(5px);
}

.modal {
  width: 500px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  padding: 0;
  border-color: rgba(226, 232, 240, 0.9);
  border-radius: 20px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.3);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 20px 22px;
  border-bottom: 1px solid #edf1f5;
}

.danger-header {
  background: linear-gradient(180deg, #fff7f7 0%, #fff 100%);
}

.modal-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.modal-icon {
  display: grid;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 12px;
  background: linear-gradient(145deg, #dbeafe, #eff6ff);
  color: #1d4ed8;
  font-size: 20px;
  font-weight: 700;
}

.modal-icon.reset {
  background: linear-gradient(145deg, #e0e7ff, #eef2ff);
  color: #4f46e5;
}

.modal-icon.danger {
  background: #fee2e2;
  color: #dc2626;
}

.modal-title {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.modal-subtitle {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}

.modal-close {
  display: grid;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  background: #f1f5f9;
  color: #64748b;
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
}

.modal-close:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.modal-body {
  padding: 20px 22px;
}

.modal-body .field:first-child {
  margin-top: 0;
}

.modal-body .input {
  width: 100%;
  min-width: 0;
  background: #fff;
}

.label {
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}

.error {
  color: #dc2626;
}

.target-card {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 15px;
  border: 1px solid #dbe4f0;
  border-radius: 14px;
  background: #f8fafc;
}

.target-card.danger {
  border-color: #fecaca;
  background: #fffafa;
}

.target-avatar {
  flex-basis: 44px;
  width: 44px;
  height: 44px;
}

.target-avatar.danger {
  border-color: #fecaca;
  background: #fee2e2;
  color: #b91c1c;
}

.target-info {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.target-info > span {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
}

.target-info strong {
  color: #0f172a;
  font-size: 15px;
}

.target-info small {
  overflow: hidden;
  color: #64748b;
  font-size: 12px;
  text-overflow: ellipsis;
}

.notice-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 14px;
  padding: 13px 14px;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  background: #eff6ff;
}

.notice-card.danger {
  border-color: #fecaca;
  background: #fff1f2;
}

.notice-icon {
  display: grid;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  place-items: center;
  border-radius: 50%;
  background: #2563eb;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}

.notice-icon.danger {
  background: #dc2626;
}

.notice-card strong {
  color: #1e3a8a;
  font-size: 12px;
}

.notice-card.danger strong {
  color: #991b1b;
}

.notice-card p {
  margin: 3px 0 0;
  color: #475569;
  font-size: 12px;
  line-height: 1.5;
}

.modal-actions {
  margin-top: 0;
  padding: 15px 22px 18px;
  border-top: 1px solid #edf1f5;
}

@media (max-width: 980px) {
  .page {
    padding: 20px 6px 32px;
  }

  .h2 {
    font-size: 24px;
  }

  .panel {
    padding: 14px;
  }
}

@media (max-width: 640px) {
  .head {
    align-items: stretch;
    flex-direction: column;
  }

  .head-actions {
    flex-direction: column;
  }

  .member-summary,
  .head-actions .btn-primary {
    width: 100%;
  }

  .head-actions .btn-primary {
    justify-content: center;
  }

  .toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .tool-left,
  .tool-left .input,
  .tool-right,
  .tool-right .btn {
    width: 100%;
  }

  .modal-overlay {
    padding: 8px;
  }

  .modal {
    width: 100%;
    max-height: calc(100vh - 16px);
  }

  .modal-header {
    padding: 16px;
  }

  .modal-body {
    padding: 16px;
  }

  .modal-actions {
    padding: 14px 16px 16px;
  }

  .modal-actions .btn {
    flex: 1;
  }
}
</style>
